/**
 * RoleAwareNav — pure data describing which sidebar nav items each
 * role sees. Kept separate from <Sidebar /> so the visual component
 * stays dumb and this business-logic mapping is easy to change without
 * touching layout.
 *
 * Decisions baked in (see chat with PM):
 *   - TM does NOT see Dashboard (BIT 5: "TM lands on Job Sheet")
 *   - Reports is Admin + Super only (BIT 14)
 *   - Doubts Inbox is Admin + Super only (BIT 9)
 *   - Approvals is Super only (BIT 11)
 *   - Events is shown to all roles but disabled (Phase 2 — BIT 17)
 *
 * Routes correspond to the App.jsx route tree:
 *   Super:  /super-admin/<segment>
 *   Admin:  /admin/<segment>
 *   TL:     /tl/<segment>
 *   TM:     /member/<segment>
 */

import { ROLES, getRolePrefix } from '../../utils/roleUtils.js';

// All possible nav items. `showFor` is an allow-list of roles.
// `disabled` items render but are not clickable.
const NAV_ITEMS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    iconName: 'dash',
    segment: 'dashboard',
    showFor: [ROLES.SUPER, ROLES.ADMIN, ROLES.TL],
  },
  {
    id: 'job-sheet',
    label: 'Job Sheet',
    iconName: 'sheet',
    segment: 'job-sheet',
    showFor: [ROLES.SUPER, ROLES.ADMIN, ROLES.TL, ROLES.TM],
  },
  {
    id: 'doubts',
    label: 'Doubts Inbox',
    iconName: 'doubts',
    segment: 'doubts',
    showFor: [ROLES.SUPER, ROLES.ADMIN],
  },
  {
    id: 'reports',
    label: 'Reports',
    iconName: 'reports',
    segment: 'reports',
    showFor: [ROLES.SUPER, ROLES.ADMIN],
  },
  {
    id: 'team',
    label: 'Team',
    iconName: 'team',
    segment: 'team',
    showFor: [ROLES.SUPER, ROLES.ADMIN, ROLES.TL, ROLES.TM],
  },
  {
    id: 'events',
    label: 'Events',
    iconName: 'cal',
    segment: 'events',
    showFor: [ROLES.SUPER, ROLES.ADMIN, ROLES.TL, ROLES.TM],
    disabled: true, // Phase 2 — BIT 17
  },
];

// Admin section (visually separated in the sidebar)
const ADMIN_NAV_ITEMS = [
  {
    id: 'approvals',
    label: 'Approvals',
    iconName: 'approvals',
    segment: 'approvals',
    showFor: [ROLES.SUPER],
  },
];

/**
 * Returns the nav items for a role, with `to` paths pre-computed.
 *
 * @param {string} role
 * @returns {{ workspace: NavItem[], admin: NavItem[] }}
 */
export function getNavItems(role) {
  const prefix = getRolePrefix(role);

  const filter = (item) => item.showFor.includes(role);
  const withTo = (item) => ({ ...item, to: `${prefix}/${item.segment}` });

  return {
    workspace: NAV_ITEMS.filter(filter).map(withTo),
    admin: ADMIN_NAV_ITEMS.filter(filter).map(withTo),
  };
}